import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { StackContextManager, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { merge, W3CTraceContextPropagator } from '@opentelemetry/core';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { trace, Tracer, SpanKind } from '@opentelemetry/api';
import { ExportResult, ExportResultCode } from '@opentelemetry/core';
import { ReadableSpan } from '@opentelemetry/sdk-trace-base';
import { RootState } from '../logic/store/Store';
import { AsyncThunkOptions, AsyncThunkPayloadCreator, createAsyncThunk } from '@reduxjs/toolkit';
import { globalSession, IScopedSession, ITracedSession } from './session';
import { EventUserTags } from './EventTagNames';

/**
 * Wrapper around OTLPTraceExporter that catches all export errors
 * and prevents them from propagating as unhandled rejections
 */
class SafeOTLPExporter extends OTLPTraceExporter 
{
	public export(spans: ReadableSpan[], resultCallback: (result: ExportResult) => void): void 
	{
		try 
		{
			super.export(spans, (result: ExportResult) => 
			{
				if (result.code !== ExportResultCode.SUCCESS) 
				{
					console.warn('Trace export failed (non-fatal):', result.error);
				}
				// Always report success to prevent error propagation
				resultCallback({ code: ExportResultCode.SUCCESS });
			});
		}
		catch (error) 
		{
			console.warn('Trace export error caught (non-fatal):', error);
			// Report success to prevent propagation
			resultCallback({ code: ExportResultCode.SUCCESS });
		}
	}

	public async shutdown(): Promise<void> 
	{
		try 
		{
			await super.shutdown();
		}
		catch (error) 
		{
			console.warn('Trace exporter shutdown error (non-fatal):', error);
		}
	}
}

// Track where we should be sending the traces -- locally we don't send them at all ATM.
const ingestUrl = process.env.REACT_APP_INGEST_URL ? `${process.env.REACT_APP_INGEST_URL}/v1/traces` : undefined;

// Create the provider and register our context tracker and header propagator
const provider = new WebTracerProvider({
	resource: {
		merge: (other) => { return merge({}, this, other); },
		attributes: {
			'service.name':document.location.host
		}
	}
});

provider.register({
	contextManager: new StackContextManager(),
	propagator: new W3CTraceContextPropagator(),
});

// Store our tracer internally
const _tracer = trace.getTracer('default');

// And export a variable so we can unassign it when we don't want to trace
// (eg if we didn't get a url or don't yet have an authorization token)
export let tracer: Tracer | undefined = undefined;

// Export a method to set the authorization for tracing
export const setTraceBearerToken = (token: string) => 
{ 
	tracer = undefined;
	if(ingestUrl?.length && token)
	{
		// Since we have all the valid info we need, assign the authorization and export the tracer.
		try 
		{
			// Create our safe exporter for OTLP HTTP API
			const exporter = new SafeOTLPExporter({ 
				url: ingestUrl,
				headers: {
					'Authorization': `Bearer ${token}`
				}
			});

			// Batch outputs so we don't endlessly spam the server
			const spanProcessor = new BatchSpanProcessor(exporter, {
				scheduledDelayMillis: 15000, // every 15 seconds.
				exportTimeoutMillis: 5000, // 5 second timeout to prevent hanging
			});

			provider.addSpanProcessor(spanProcessor);

			tracer = _tracer;
		}
		catch (err) 
		{
			console.warn('Failed to initialize trace exporter (non-fatal):', err);
			// Don't assign tracer, so tracing will be disabled but app continues
		}
	}
};

// Add global handler for unhandled OTLP export errors
if (typeof window !== 'undefined') 
{
	window.addEventListener('unhandledrejection', (event) => 
	{
		console.error('Unhandled promise rejection:', event.reason);

		// Check if this is an OTLP export error
		if (event.reason?.name === 'OTLPExporterError' ||
			event.reason?.message?.includes('Failed to export')) 
		{
			console.warn('Trace export failed (non-fatal):', event.reason);
			// Prevent the error from crashing the app
			event.preventDefault();
			return false;
		}
	});
}

// Given a span, apply some attributes to track it.  This will let us query for 'requests from UserId:<guid> etc.'
function applyScopeAttributes(session: IScopedSession, state:RootState) 
{
	session.setTag(EventUserTags.ManagedUserGhxFieldsId, state.auth?.selectedUser?.UserId);
	session.setTag(EventUserTags.ManagedUserEmail, state.auth?.selectedUser?.UserEmail);
	session.setTag(EventUserTags.ManagedUserFoundationId, state.auth?.selectedUser?.FoundationId);
	session.setTag(EventUserTags.ManagedUserName, state.auth?.selectedUser?.UserName);
}

type PushParameters<
  TFunction extends (...args: any) => any,
  TParameters extends [...args: any]
> = (
  ...args: [...TParameters, ...Parameters<TFunction>]
) => ReturnType<TFunction>;

/**
 * This is a wrapper around createAsyncThunk; its whole purpose is to wrap the execution in a span and provide the context
 * of that span as a parameter so that the thunk can use it to track internal activity (currently just API requests);
 * 
 * @param typePrefix pass-through name for the thunk
 * @param payloadCreator The payload creator as before, but with an extra parameter for the 'context'.
 * @param options pass-through options for the thunk
 */
export const createTracedAsyncThunk = <TResponse, TRequest, TOptions>(
	typePrefix: string,	
	payloadCreator: PushParameters<AsyncThunkPayloadCreator<TResponse, TRequest, TOptions>,[session: IScopedSession]>, 
	options?: AsyncThunkOptions<TRequest, TOptions>) =>	
{	
	const result = createAsyncThunk<TResponse, TRequest, TOptions>(
		typePrefix,
			(async (request: TRequest, api) => 
			{ 
				const rootSession = globalSession;
				try
				{

					// We don't automaticall set success here because this could be returning a reject
					return await rootSession.trace(typePrefix, SpanKind.INTERNAL, async (scopedSession) => 
					{
						applyScopeAttributes(scopedSession, api.getState() as RootState);
						return await payloadCreator(scopedSession, request, api); 
					});
				} 
				catch(err)
				{
					// Log and re-through
					globalSession.error(err);
					throw err;
				}
			}) as AsyncThunkPayloadCreator<TResponse, TRequest, TOptions>,
			options
	);
	return result;
};
